import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, X, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { findCollection, PRICE } from "../data/collections";
import { whatsappLink, colorOf, PERSONALITIES } from "../data/site";
import { useApp } from "../context/AppContext";
import { WhatsAppIcon } from "./Illustrations";
import { Star } from "./Illustrations";
import { PrimaryDoodle, SecondaryDoodle } from "./PersonalityDoodles";

/** Premium quick-view modal — opens from any collection card. */
export default function QuickViewModal({ slug, open, onOpenChange }) {
  const c = findCollection(slug);
  const [color, setColor] = useState(c?.colors?.[0]);
  const { isWished, toggleWish } = useApp();

  useEffect(() => { if (c) setColor(c.colors[0]); }, [slug, c]);

  if (!c) return null;

  const personality = PERSONALITIES.find((p) => p.slug === c.personality);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[920px] p-0 overflow-hidden bg-[#FAFAF7] border-0 rounded-[24px]"
        data-testid={`quick-view-${c.slug}`}
      >
        <DialogTitle className="sr-only">{c.name} — quick view</DialogTitle>
        <DialogDescription className="sr-only">{c.tagline[0]} — {c.audience}, {PRICE}. Shipping charges extra, as applicable.</DialogDescription>
        <div className="grid md:grid-cols-2">
          <QVImage c={c} />

          {/* Details */}
          <div className="relative p-7 md:p-9 flex flex-col">
            <SecondaryDoodle personality={c.personality} className="!absolute !top-6 !right-6 opacity-80" />

            <p className="eyebrow mb-2">{c.audience}</p>
            <h3 className="font-serif text-4xl md:text-5xl tracking-[-0.025em] leading-none">{c.name}</h3>
            <div className="mt-4 space-y-1 text-[#2C2C2A]/85 text-base md:text-lg leading-snug">
              {c.tagline.map((t) => <p key={t}>{t}</p>)}
            </div>

            <div className="mt-5 flex items-center gap-3">
              <span className="font-serif text-2xl">{PRICE}</span>
              <span className="text-[11px] text-[#595955] uppercase tracking-[0.18em]">Shipping charges extra</span>
            </div>

            <QVSwatches colors={c.colors} active={color} onPick={setColor} />
            <QVSpecs c={c} />
            <QVActions c={c} color={color} wished={isWished(c.slug)} onWish={() => toggleWish(c.slug)} onClose={() => onOpenChange(false)} />

            {personality && (
              <Link
                to={`/personality/${personality.slug}`}
                className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#595955] ink-link"
                onClick={() => onOpenChange(false)}
              >
                {personality.emoji} For {personality.title.toLowerCase()} →
              </Link>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QVImage({ c }) {
  return (
    <div className="relative aspect-square md:aspect-auto bg-[#F3EADA] overflow-hidden">
      <img
        src={`/products/${c.slug}-kid.jpg`}
        alt={`${c.name} sunglasses`}
        className="w-full h-full object-cover"
      />
      <div className="absolute top-4 left-4">
        <PrimaryDoodle personality={c.personality} className="!w-12 !h-12" />
      </div>
      <Star size={20} color="#F4C734" className="absolute top-6 right-12 wiggle" />
    </div>
  );
}

function QVSwatches({ colors, active, onPick }) {
  return (
    <div className="mt-6">
      <p className="eyebrow mb-2">Colour <span className="text-[#2C2C2A] tracking-normal normal-case font-medium text-[13px] ml-1">{active}</span></p>
      <div className="flex flex-wrap gap-2">
        {colors.map((col) => (
          <button
            key={col}
            onClick={() => onPick(col)}
            className={`w-8 h-8 rounded-full transition-all ${active === col ? "ring-2 ring-[#2C2C2A] ring-offset-2 ring-offset-[#FAFAF7]" : "ring-1 ring-[#2C2C2A]/15 hover:scale-105"}`}
            style={{ background: colorOf(col) }}
            aria-label={col}
            data-testid={`qv-swatch-${col.replace(/\s+/g, "-")}`}
          />
        ))}
      </div>
    </div>
  );
}

function QVSpecs({ c }) {
  return (
    <div className="mt-6 grid grid-cols-3 gap-3 text-[12px] text-[#595955]">
      <div><span className="eyebrow block mb-1">Width</span>{c.dims.width} cm</div>
      <div><span className="eyebrow block mb-1">Arm</span>{c.dims.arm} cm</div>
      <div><span className="eyebrow block mb-1">Age</span>{c.age} yrs</div>
    </div>
  );
}

function QVActions({ c, color, wished, onWish, onClose }) {
  const buyMsg = `Hi SOL Kids! I'd love to order the ${c.name} sunglasses in ${color?.toUpperCase()} colour. (${PRICE})`;
  return (
    <div className="mt-7 flex flex-wrap gap-2 mt-auto">
      <a
        href={whatsappLink(buyMsg)} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-[14px] font-medium hover:brightness-95"
        data-testid={`qv-buy-${c.slug}`}
      >
        <WhatsAppIcon size={15} /> Buy on WhatsApp
      </a>
      <Link
        to={`/collection/${c.slug}`}
        onClick={onClose}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-5 py-3 text-[14px] font-medium hover:bg-[#1A1A18]"
        data-testid={`qv-details-${c.slug}`}
      >
        Full details <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
      <button
        onClick={onWish}
        className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${wished ? "bg-[#2C2C2A] text-[#FAFAF7]" : "bg-transparent border border-[#2C2C2A]/15 text-[#2C2C2A] hover:border-[#2C2C2A]"}`}
        aria-label={wished ? "Remove from wishlist" : "Save to wishlist"}
        data-testid={`qv-wish-${c.slug}`}
      >
        <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} />
      </button>
    </div>
  );
}
